import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Alert, Button, Checkbox, Space, Spin, Typography } from 'antd'
import {
  configureCollectJs,
  DEFAULT_BANCCARD_COLLECT_JS_URL,
  loadCollectJs,
  resetCollectJs,
  type CollectJsPayment,
} from '../../utils/collectJs'

const { Text } = Typography

export interface BanccardPayCheckoutProps {
  checkoutToken: string
  secretToken: string
  tokenizationKey: string
  collectJsUrl?: string
  amountLabel?: string
  preferredPayment?: CollectJsPayment
  allowAch?: boolean
  onValidate: (payload: {
    rawDataResponse: string
    checkoutToken: string
    secretToken: string
  }) => Promise<void>
  onSuccess: () => void
  onError: (message: string) => void
}

export function BanccardPayCheckout({
  checkoutToken,
  secretToken,
  tokenizationKey,
  collectJsUrl,
  amountLabel,
  preferredPayment,
  allowAch,
  onValidate,
  onSuccess,
  onError,
}: BanccardPayCheckoutProps) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [saveCard, setSaveCard] = useState(false)
  const [payment, setPayment] = useState<CollectJsPayment>(preferredPayment || 'creditcard')
  const [fieldsReady, setFieldsReady] = useState(false)
  const onErrorRef = useRef(onError)
  const submitTokenRef = useRef<(token: string) => Promise<void>>(async () => undefined)
  onErrorRef.current = onError

  useEffect(() => {
    setLoading(false)
    return () => resetCollectJs()
  }, [collectJsUrl, tokenizationKey])

  useEffect(() => {
    setFieldsReady(false)
    if (loading) return
    const id = window.requestAnimationFrame(() => setFieldsReady(true))
    return () => window.cancelAnimationFrame(id)
  }, [loading, payment])

  useEffect(() => {
    if (loading || !fieldsReady) return
    let cancelled = false
    const mountFields = async () => {
      try {
        resetCollectJs()
        await loadCollectJs(collectJsUrl || DEFAULT_BANCCARD_COLLECT_JS_URL, tokenizationKey)
        if (cancelled) return
        configureCollectJs({
          payment,
          tokenizationKey,
          onToken: (token) => {
            void submitTokenRef.current(token)
          },
          onError: (message) => onErrorRef.current(message),
        })
      } catch (err: unknown) {
        onErrorRef.current(err instanceof Error ? err.message : 'Failed to load BancCard checkout')
      }
    }
    void mountFields()
    return () => {
      cancelled = true
    }
  }, [loading, fieldsReady, payment, collectJsUrl, tokenizationKey])

  const submitToken = async (paymentToken: string) => {
    setSubmitting(true)
    try {
      await onValidate({
        checkoutToken,
        secretToken,
        rawDataResponse: JSON.stringify({
          payment_token: paymentToken,
          payment,
          saveCard,
          secCode: payment === 'check' ? 'WEB' : undefined,
        }),
      })
      onSuccess()
    } catch (err: unknown) {
      onErrorRef.current(err instanceof Error ? err.message : 'BancCard payment failed')
    } finally {
      setSubmitting(false)
    }
  }
  submitTokenRef.current = submitToken

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
        <div style={{ marginTop: 8 }}>Loading secure payment form...</div>
      </div>
    )
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {amountLabel && <Alert type="info" showIcon message={amountLabel} />}
      {allowAch && (
        <Space>
          <Button
            type={payment === 'creditcard' ? 'primary' : 'default'}
            onClick={() => setPayment('creditcard')}
          >
            Card
          </Button>
          <Button type={payment === 'check' ? 'primary' : 'default'} onClick={() => setPayment('check')}>
            Bank account
          </Button>
        </Space>
      )}

      {payment === 'creditcard' ? (
        <div key="card-fields">
          <FieldLabel htmlFor="banccard-ccnumber">Card number</FieldLabel>
          <div id="banccard-ccnumber" style={fieldStyle} />
          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel htmlFor="banccard-ccexp">Expiration</FieldLabel>
              <div id="banccard-ccexp" style={fieldStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel htmlFor="banccard-cvv">CVV</FieldLabel>
              <div id="banccard-cvv" style={fieldStyle} />
            </div>
          </div>
        </div>
      ) : (
        <div key="ach-fields">
          <FieldLabel htmlFor="banccard-checkname">Name on account</FieldLabel>
          <div id="banccard-checkname" style={fieldStyle} />
          <div style={{ marginTop: 12 }}>
            <FieldLabel htmlFor="banccard-checkaba">Routing number</FieldLabel>
            <div id="banccard-checkaba" style={fieldStyle} />
          </div>
          <div style={{ marginTop: 12 }}>
            <FieldLabel htmlFor="banccard-checkaccount">Account number</FieldLabel>
            <div id="banccard-checkaccount" style={fieldStyle} />
          </div>
        </div>
      )}

      <Checkbox checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)}>
        Save this payment method for next time
      </Checkbox>

      <Button id="banccard-pay-button" type="primary" size="large" loading={submitting} block style={{ height: 48, fontSize: 16 }}>
        Pay now
      </Button>
      <Text type="secondary">Card details are tokenized by BancCard and never stored by ServiceFlow.</Text>
    </Space>
  )
}

const fieldStyle: React.CSSProperties = {
  minHeight: 48,
  border: '1px solid #d1d5db',
  borderRadius: 10,
  padding: 0,
  background: '#fff',
  overflow: 'hidden',
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: 'block',
        fontSize: 12,
        fontWeight: 600,
        color: '#374151',
        marginBottom: 6,
      }}
    >
      {children}
    </label>
  )
}
